import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
  Brush,
  ResponsiveContainer,
} from 'recharts';
import { MenuItem, Select } from '@mui/material';
import { scaleOrdinal } from 'd3-scale';
import { schemeDark2 } from 'd3-scale-chromatic';
import { styled } from '@mui/material/styles';
//
import {
  BASE_CHART_LINE_COLORS,
  REPORT_CHART_FORMATTERS_BY_METRICS_TYPE,
  REPORT_CHART_METRICS_TYPE_OPTIONS,
} from '../../../constants/reports';
import PendingContainer from 'components/common/PendingContainer';
import { NoDataOverlay } from 'components/common/NoDataOverlay';
import { CustomLegend } from './components/CustomLegend';
import { CustomTooltip } from './components/CustomTooltip';
import { calculateMaxY, roundUpToNiceNumber } from 'utils/reportsChart';

const d3ColorScale = scaleOrdinal(schemeDark2);

const ChartContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  rowGap: '16px',
  padding: '32px 16px',
  width: '100%',
}));

export const ReportsChart = ({
  isRequestPending,
  chartMetricsType,
  changeMetricsType,
  chartConfig,
  groupBy,
}) => {
  const isEmpty = !chartConfig.chartData || chartConfig.chartData.length === 0;

  const getLineColorByIndex = (index = 0) => {
    return index < BASE_CHART_LINE_COLORS.length
      ? BASE_CHART_LINE_COLORS[index]
      : d3ColorScale(index - BASE_CHART_LINE_COLORS.length);
  };

  const paddedMaxY = useMemo(() => {
    const maxY = calculateMaxY(chartConfig.lines, chartConfig.chartData);

    const paddedY = Math.ceil(maxY * 1.1);

    return roundUpToNiceNumber(paddedY);
  }, [chartConfig]);

  return (
    <PendingContainer
      isPending={isRequestPending}
      containerStyle={{ overflow: 'visible' }}
    >
      <ChartContainer>
        <NoDataOverlay show={!isRequestPending && isEmpty}>
          <div style={{ width: '100%' }}>
            <ResponsiveContainer width='100%' height={500}>
              <LineChart
                height={500}
                data={chartConfig.chartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='date' />
                <YAxis domain={[0, paddedMaxY]} allowDataOverflow />
                <Tooltip
                  content={CustomTooltip}
                  wrapperStyle={{ zIndex: 10 }}
                  groupBy={groupBy}
                  formatter={
                    REPORT_CHART_FORMATTERS_BY_METRICS_TYPE[chartMetricsType]
                  }
                />
                <Legend
                  content={groupBy === 'geo' ? CustomLegend : undefined}
                  wrapperStyle={{ bottom: -4 }}
                />
                {chartConfig &&
                  Array.isArray(chartConfig.lines) &&
                  chartConfig.lines.map((lineName, index) => (
                    <Line
                      type='monotone'
                      key={lineName}
                      dataKey={lineName}
                      strokeWidth={2}
                      activeDot={{ r: 4 }}
                      stroke={getLineColorByIndex(index)}
                    />
                  ))}
                <Brush />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </NoDataOverlay>

        <Select
          style={{ minWidth: '300px' }}
          id='chartMetricsType'
          label='Metric'
          variant='standard'
          multiple={false}
          labelId='chartMetricsType'
          onChange={(event) => {
            changeMetricsType(event.target.value);
          }}
          value={chartMetricsType}
        >
          {REPORT_CHART_METRICS_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </ChartContainer>
    </PendingContainer>
  );
};
